import { useMemo, useState } from 'react';

import { ApprovalStatus, BillingGroup, DateStr, GUID } from '@ndvr/billing-api';

import { BulkSelectionBar } from '@/components/BulkSelectionBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';
import { pluralize } from '@/utils/string-utils';

import { useApproveBillingGroupRuns, useRerunBillingGroups } from './billing-run-queries';
import { RerunResult, RerunResultDialog } from './RerunResultDialog';
import { BillingRunSelection, isApprovableRun } from './useBillingRunSelection';
import { billingDateLabel } from './utils';

type BillingRunBulkActionsProps = {
    billingDate: DateStr;
    canRerun: boolean;
    selection: BillingRunSelection;
};

const approvalConfirmationDescription = (
    billingDate: DateStr,
    approvableCount: number,
    alreadyApprovedCount: number,
    otherIneligibleCount: number,
) => {
    const excludedCount = alreadyApprovedCount + otherIneligibleCount;
    const exclusionMessage =
        alreadyApprovedCount > 0 && otherIneligibleCount === 0
            ? ` ${pluralize(alreadyApprovedCount, 'selected billing run')} ${alreadyApprovedCount === 1 ? 'is' : 'are'} already approved and will be left unchanged.`
            : excludedCount > 0
              ? ` ${pluralize(excludedCount, 'selected billing run')} cannot be approved and will be left unchanged.`
              : '';

    return `Approve ${pluralize(approvableCount, 'billing run')} for ${billingDateLabel(billingDate)}?${exclusionMessage}`;
};

export const BillingRunBulkActions = ({ billingDate, canRerun, selection }: BillingRunBulkActionsProps) => {
    const { selectedRuns, setRunsSelected, replaceSelection, clearSelection } = selection;
    const [approveConfirmationOpen, setApproveConfirmationOpen] = useState(false);
    const [rerunConfirmationOpen, setRerunConfirmationOpen] = useState(false);
    const [rerunResult, setRerunResult] = useState<RerunResult>();
    const approveRuns = useApproveBillingGroupRuns();
    const rerunGroups = useRerunBillingGroups();
    const isMutating = approveRuns.isPending || rerunGroups.isPending;

    const approvableRunGUIDs = useMemo(
        () => selectedRuns.filter(isApprovableRun).map((run) => run.billingGroupRunGUID),
        [selectedRuns],
    );
    const selectedBillingGroupGUIDs = useMemo(
        () => [...new Set(selectedRuns.map((run) => run.billingGroupGUID))] as GUID<BillingGroup>[],
        [selectedRuns],
    );
    const alreadyApprovedCount = selectedRuns.filter((run) => run.approvalStatus === ApprovalStatus.APPROVED).length;
    const otherIneligibleCount = selectedRuns.length - approvableRunGUIDs.length - alreadyApprovedCount;

    const approve = () =>
        approveRuns.mutate(approvableRunGUIDs, {
            onSuccess: (_data, approvedRunGUIDs) => {
                setApproveConfirmationOpen(false);
                setRunsSelected(approvedRunGUIDs, false);
            },
        });

    const rerun = () =>
        rerunGroups.mutate(
            { billingDate, billingGroupGUIDs: selectedBillingGroupGUIDs },
            {
                onSuccess: (skippedGroups) => {
                    setRerunConfirmationOpen(false);
                    if (skippedGroups.length === 0) {
                        clearSelection();
                        return;
                    }

                    const runByGroupGUID = new Map(selectedRuns.map((run) => [run.billingGroupGUID, run]));
                    replaceSelection(
                        skippedGroups.flatMap((group) => {
                            const run = runByGroupGUID.get(group.billingGroupGUID);
                            return run ? [run.billingGroupRunGUID] : [];
                        }),
                    );
                    setRerunResult({
                        rerunCount: selectedBillingGroupGUIDs.length - skippedGroups.length,
                        skippedGroups: skippedGroups.map((group) => ({
                            billingGroupGUID: group.billingGroupGUID,
                            billingGroupName:
                                runByGroupGUID.get(group.billingGroupGUID)?.billingGroupName ?? group.billingGroupGUID,
                            reason: group.reason,
                        })),
                    });
                },
            },
        );

    return (
        <>
            {selectedRuns.length > 0 && (
                <BulkSelectionBar
                    selectionLabel={pluralize(selectedRuns.length, 'billing run', 'billing runs')}
                    onClear={clearSelection}
                >
                    {approvableRunGUIDs.length > 0 && (
                        <Button size="sm" disabled={isMutating} onClick={() => setApproveConfirmationOpen(true)}>
                            Approve{approvableRunGUIDs.length > 1 ? ` (${approvableRunGUIDs.length})` : ''}
                        </Button>
                    )}
                    {canRerun && (
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={isMutating}
                            onClick={() => setRerunConfirmationOpen(true)}
                        >
                            Rerun
                        </Button>
                    )}
                </BulkSelectionBar>
            )}

            <ConfirmModal
                open={approveConfirmationOpen}
                onOpenChange={setApproveConfirmationOpen}
                title="Approve Billing Runs"
                description={approvalConfirmationDescription(
                    billingDate,
                    approvableRunGUIDs.length,
                    alreadyApprovedCount,
                    otherIneligibleCount,
                )}
                confirmText="Approve"
                onConfirm={approve}
                loading={approveRuns.isPending}
                closeOnConfirm={false}
            />

            <ConfirmModal
                open={rerunConfirmationOpen}
                onOpenChange={setRerunConfirmationOpen}
                title="Rerun Billing Groups"
                description={`Rerun ${pluralize(selectedBillingGroupGUIDs.length, 'billing group', 'billing groups')} for ${billingDateLabel(billingDate)} using their current configurations?`}
                confirmText="Rerun"
                onConfirm={rerun}
                loading={rerunGroups.isPending}
                closeOnConfirm={false}
            />

            <RerunResultDialog result={rerunResult} onClose={() => setRerunResult(undefined)} />
        </>
    );
};
